import { TFunction } from 'i18next';
import { CallActivityEventType, CallActivityLogEntry } from '../../services/api/call-log.types';
import { formatPhoneIfPresent } from '../../utils/format-phone.util';

export interface EventDetailField {
  label: string;
  value: string;
}

export interface EventDetailList {
  label: string;
  items: string[];
}

export type EventDetailEntry = EventDetailField | EventDetailList;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | undefined {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }
  return String(value);
}

function pushField(
  entries: EventDetailEntry[],
  label: string,
  value: unknown,
  format?: (value: unknown) => string | undefined
): void {
  const formatted = format ? format(value) : asString(value);
  if (!formatted) {
    return;
  }
  entries.push({ label, value: formatted });
}

function pushPhoneField(entries: EventDetailEntry[], label: string, value: unknown): void {
  pushField(entries, label, value, (raw) => formatPhoneIfPresent(String(raw)));
}

function formatBoolean(value: unknown, t: TFunction): string | undefined {
  if (typeof value !== 'boolean') {
    return asString(value);
  }
  return value ? t('details.yes') : t('details.no');
}

function formatContact(contact: unknown, t: TFunction): string | undefined {
  if (!isRecord(contact)) {
    return undefined;
  }

  const explicitName = asString(contact.name);
  const composedName =
    `${asString(contact.firstName) ?? ''} ${asString(contact.lastName) ?? ''}`.trim();
  const name =
    explicitName || composedName || asString(contact.accountName) || t('details.unknownContact');
  const phone = formatPhoneIfPresent(asString(contact.phone));
  const id = asString(contact.id);

  if (phone && id) {
    return t('details.contactEntryWithId', { name, phone, id });
  }
  if (phone) {
    return t('details.contactEntry', { name, phone });
  }
  if (id) {
    return t('details.contactEntryIdOnly', { name, id });
  }
  return name;
}

function formatContacts(contacts: unknown, t: TFunction): string[] {
  if (!Array.isArray(contacts)) {
    return [];
  }

  return contacts
    .map((contact) => formatContact(contact, t))
    .filter((entry): entry is string => Boolean(entry));
}

function formatParty(party: unknown, t: TFunction): string | undefined {
  if (!isRecord(party)) {
    return undefined;
  }

  const name = asString(party.name) ?? t('details.unknownParty');
  const phoneNumber = formatPhoneIfPresent(asString(party.phoneNumber));
  const partyType = asString(party.partyType);
  const isOriginalCaller = party.isOriginalCaller === true;

  const parts = [name];
  if (phoneNumber) {
    parts.push(phoneNumber);
  }
  if (partyType) {
    parts.push(partyType);
  }
  if (isOriginalCaller) {
    parts.push(t('details.originalCaller'));
  }

  return parts.join(' · ');
}

function formatDisposition(disposition: unknown, t: TFunction): EventDetailEntry[] {
  if (!isRecord(disposition)) {
    return [];
  }

  const entries: EventDetailEntry[] = [];
  pushField(entries, t('details.dispositionName'), disposition.name);
  pushField(entries, t('details.dispositionNotes'), disposition.notes);
  pushField(entries, t('details.dispositionId'), disposition.id ?? disposition.code);
  return entries;
}

function detailsForCallReceived(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const direction = asString(payload.direction);

  if (direction) {
    pushField(entries, t('details.direction'), t(`direction.${direction}`, direction));
  }

  pushPhoneField(entries, t('details.phone'), payload.phone);
  pushField(entries, t('details.callerName'), payload.callerName);
  pushField(entries, t('details.skillId'), payload.skillId);
  pushPhoneField(entries, t('details.dnis'), payload.dnis);
  pushField(entries, t('details.status'), payload.status);
  pushField(entries, t('details.sapContactId'), payload.sapContactId);
  pushField(entries, t('details.referenceId'), payload.referenceId);
  pushField(entries, t('details.autoAccept'), payload.isAutoAccept, (value) =>
    formatBoolean(value, t)
  );

  return entries;
}

function detailsForCallAnswered(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const direction = asString(payload.direction);

  if (direction) {
    pushField(entries, t('details.direction'), t(`direction.${direction}`, direction));
  }

  pushPhoneField(entries, t('details.phone'), payload.phone);
  pushField(entries, t('details.startedAt'), payload.startedAt, (value) =>
    value ? formatTraceTimeValue(value) : undefined
  );
  pushField(entries, t('details.status'), payload.status);
  return entries;
}

function detailsForTransferLike(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const targetType = asString(payload.targetType);

  if (targetType) {
    pushField(
      entries,
      t('details.targetType'),
      t(`details.targetTypeValues.${targetType}`, targetType)
    );
  }

  if (targetType === 'phone') {
    pushPhoneField(entries, t('details.phone'), payload.targetId ?? payload.targetName);
  } else {
    pushField(entries, t('details.targetName'), payload.targetName);
    pushField(entries, t('details.targetId'), payload.targetId);
  }

  return entries;
}

function detailsForConferenceUpdated(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const parties = Array.isArray(payload.parties)
    ? payload.parties
        .map((party) => formatParty(party, t))
        .filter((entry): entry is string => Boolean(entry))
    : [];

  if (parties.length === 0) {
    return [];
  }

  return [{ label: t('details.conferenceParties'), items: parties }];
}

function detailsForSapContactsDisplayed(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const source = asString(payload.source);

  if (source) {
    pushField(entries, t('details.source'), t(`details.sourceValues.${source}`, source));
  }

  pushPhoneField(entries, t('details.phone'), payload.phone);
  pushField(entries, t('details.searchQuery'), payload.query);

  const contacts = formatContacts(payload.contacts, t);
  if (contacts.length > 0) {
    entries.push({ label: t('details.contactsShown'), items: contacts });
  }

  return entries;
}

function detailsForSapContactOpened(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const source = asString(payload.source);

  if (source) {
    pushField(entries, t('details.source'), t(`details.sourceValues.${source}`, source));
  }

  const contactSummary = formatContact(payload.contact, t);
  if (contactSummary) {
    entries.push({ label: t('details.contactOpened'), items: [contactSummary] });
  }

  return entries;
}

function detailsForCallEnded(payload: Record<string, unknown>, t: TFunction): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const direction = asString(payload.direction);

  if (direction) {
    pushField(entries, t('details.direction'), t(`direction.${direction}`, direction));
  }

  pushPhoneField(entries, t('details.phone'), payload.phone);
  pushField(entries, t('details.callerName'), payload.callerName);
  pushField(entries, t('details.skillName'), payload.skillName);
  pushField(entries, t('details.dnis'), payload.dnis);
  pushField(entries, t('details.endedAt'), payload.endedAt, (value) =>
    value ? formatTraceTimeValue(value) : undefined
  );
  pushField(entries, t('details.status'), payload.status);
  return entries;
}

function detailsForDispositionCompleted(
  payload: Record<string, unknown>,
  t: TFunction
): EventDetailEntry[] {
  const entries: EventDetailEntry[] = [];
  const direction = asString(payload.direction);

  if (direction) {
    pushField(entries, t('details.direction'), t(`direction.${direction}`, direction));
  }

  pushPhoneField(entries, t('details.phone'), payload.phone);
  pushField(entries, t('details.cxoneContactId'), payload.cxoneContactId);
  pushField(entries, t('details.skillName'), payload.skillName);
  pushField(entries, t('details.skillId'), payload.skillId);
  pushField(entries, t('details.sapActivityId'), payload.sapActivityId);
  pushField(entries, t('details.selectedSapContactId'), payload.selectedSapContactId);

  entries.push(...formatDisposition(payload.disposition, t));

  const displayedContacts = formatContacts(payload.displayedContacts, t);
  if (displayedContacts.length > 0) {
    entries.push({ label: t('details.displayedContacts'), items: displayedContacts });
  }

  const openedContacts = formatContacts(payload.openedContacts, t);
  if (openedContacts.length > 0) {
    entries.push({ label: t('details.openedContacts'), items: openedContacts });
  }

  const selectedContact = formatContact(payload.selectedContact, t);
  if (selectedContact) {
    entries.push({ label: t('details.selectedContact'), items: [selectedContact] });
  }

  return entries;
}

function formatTraceTimeValue(value: unknown): string {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export function getEventDetailEntries(
  event: CallActivityLogEntry,
  t: TFunction
): EventDetailEntry[] {
  const payload = event.payload ?? {};

  switch (event.eventType as CallActivityEventType) {
    case 'CALL_RECEIVED':
      return detailsForCallReceived(payload, t);
    case 'CALL_ANSWERED':
      return detailsForCallAnswered(payload, t);
    case 'CALL_TRANSFER':
    case 'CALL_CONFERENCE_STARTED':
      return detailsForTransferLike(payload, t);
    case 'CALL_CONFERENCE_UPDATED':
      return detailsForConferenceUpdated(payload, t);
    case 'SAP_CONTACTS_DISPLAYED':
      return detailsForSapContactsDisplayed(payload, t);
    case 'SAP_CONTACT_OPENED':
      return detailsForSapContactOpened(payload, t);
    case 'CALL_ENDED':
      return detailsForCallEnded(payload, t);
    case 'DISPOSITION_COMPLETED':
      return detailsForDispositionCompleted(payload, t);
    default:
      return [];
  }
}

export function sortEventsChronologically(events: CallActivityLogEntry[]): CallActivityLogEntry[] {
  return [...events].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );
}
